import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  allShapes,
  countWords,
  isPlaceholder,
  longestParagraphWords,
  looksLikeSentence,
  readPresentation,
  slideWordCount,
} from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

/** Textplatzhalter ohne den Titel – dort steht der eigentliche Folieninhalt. */
function inhaltsplatzhalter(snapshot: Awaited<ReturnType<typeof readPresentation>>) {
  return allShapes(snapshot).filter(
    (form) =>
      isPlaceholder(form) &&
      form.paragraphs.length > 0 &&
      form.placeholder?.type !== "Title" &&
      form.placeholder?.type !== "CenterTitle" &&
      !/^(title|titel)\b/i.test(form.name ?? ""),
  );
}

export const lektionStichpunkte: Lesson = {
  id: "stichpunkte",
  title: "Eine Folie ist kein Dokument",
  summary: "Warum Fließtext auf Folien nicht funktioniert – und was stattdessen darauf gehört.",
  durationMinutes: 11,
  learningGoals: [
    "Den Unterschied zwischen Folie und Handout erklären",
    "Fließtext zu Stichpunkten verdichten",
    "Die Textmenge pro Folie im Griff behalten",
  ],
  steps: [
    {
      id: "warum-kein-fliesstext",
      title: "Warum Fließtext auf Folien nicht funktioniert",
      explanation: [
        "Der Grund ist kein Geschmacksurteil, sondern eine Eigenschaft der menschlichen " +
          "Wahrnehmung: Lesen und Zuhören greifen auf dieselbe Verarbeitung im Gehirn zu. " +
          "Wer einen Satz auf der Folie liest, kann in dieser Zeit nicht zuhören. Steht " +
          "der Vortragstext auf der Folie, konkurriert die Folie mit der vortragenden " +
          "Person – und gewinnt, weil Lesen schneller geht als Zuhören.",
        "Deshalb gilt: Auf die Folie kommt das, was das gesprochene Wort nicht leisten " +
          "kann. Zahlen, die man sich merken soll. Ein Diagramm, dessen Verlauf man sehen " +
          "muss. Eine Struktur, an der man sich orientiert. Alles andere sagst du.",
        "Der häufigste Einwand lautet: „Aber die Folien werden später verschickt, dann " +
          "muss alles drinstehen.“ Das ist ein echtes Bedürfnis – aber die falsche " +
          "Lösung. Eine Präsentation, die als Handout funktioniert, funktioniert als " +
          "Vortrag nicht mehr, und umgekehrt. Für die Nachlese gibt es die " +
          "Sprechernotizen: Sie stehen in der Notizenansicht, lassen sich als " +
          "Notizenseiten drucken und als PDF weitergeben. Der Vortrag bleibt schlank, " +
          "die Nachlese vollständig.",
      ],
      keyPoints: [
        "Lesen und Zuhören konkurrieren – Fließtext auf der Folie verdrängt den Vortrag.",
        "Auf die Folie kommt, was gesprochen nicht funktioniert: Zahlen, Bilder, Struktur.",
        "Für die Nachlese sind Sprechernotizen da, nicht die Folie selbst.",
      ],
      task: {
        instruction:
          "Sieh deine Folien durch: Auf keiner sollen mehr als 60 Wörter stehen – das " +
          "ist die Grenze, ab der das Publikum liest statt zuzuhören.",
        hint: "Gezählt wird alles in den Platzhaltern, Titel eingerechnet.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Der Text in den Platzhaltern",
              "1.4",
              "Überschlag die Wortzahl deiner vollsten Folie selbst.",
            );
          }
          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst Folien an und schreibe etwas hinein.",
            );
          }

          const gezaehlt = snapshot.slides.map((folie) => ({
            nummer: folie.index + 1,
            woerter: slideWordCount(folie),
          }));
          const zuVoll = gezaehlt.filter((eintrag) => eintrag.woerter > 60);

          if (zuVoll.length === 0) {
            const vollste = gezaehlt.reduce((a, b) => (a.woerter >= b.woerter ? a : b));
            return pass(
              `Die vollste Folie ist Folie ${vollste.nummer} mit ${vollste.woerter} Wörtern – noch unter der Grenze.`,
            );
          }

          const schlimmste = zuVoll.reduce((a, b) => (a.woerter >= b.woerter ? a : b));
          return fail(
            `Auf Folie ${schlimmste.nummer} stehen ${schlimmste.woerter} Wörter.` +
              (zuVoll.length > 1 ? ` Insgesamt betrifft das ${zuVoll.length} Folien.` : ""),
            "Was du streichst, ist nicht verloren: Es gehört in die Sprechernotizen. Passt es inhaltlich nicht zusammen, teile die Folie.",
          );
        },
      },
    },
    {
      id: "keine-saetze",
      title: "Stichpunkte statt Sätze",
      explanation: [
        "Ein Stichpunkt ist die Kurzform einer Aussage: „Umsatz +12 % gegenüber Vorjahr“ " +
          "statt „Der Umsatz ist im Vergleich zum Vorjahr um 12 Prozent gestiegen.“ " +
          "Beides trägt dieselbe Information, aber die Kurzform lässt sich in einem Blick " +
          "erfassen.",
        "Zwei Faustregeln helfen beim Kürzen. Erstens: Was am Ende einen Punkt braucht, " +
          "ist ein Satz und damit zu lang. Zweitens: Mehr als etwa zehn Wörter " +
          "pro Stichpunkt bekommt niemand im Vorbeigehen mit.",
        "Beim Kürzen fallen zuerst Füllwörter und Verbindungen weg („im Vergleich zu“, " +
          "„es zeigt sich, dass“), dann Hilfsverben, zuletzt der Artikel. Was bleibt, ist " +
          "der Kern – und der ist meistens auch die Aussage, die du sowieso sagen wolltest.",
      ],
      keyPoints: [
        "Kein Punkt am Ende – ein Stichpunkt ist kein Satz.",
        "Als Richtwert höchstens zehn Wörter pro Stichpunkt.",
        "Zuerst Füllwörter streichen, dann Hilfsverben, zuletzt Artikel.",
      ],
      task: {
        instruction:
          "Kürze den Text in deinen Inhaltsplatzhaltern so, dass kein Stichpunkt mehr als " +
          "zehn Wörter hat und keiner als ganzer Satz mit Punkt endet.",
        hint: "Ein Punkt am Ende ist das verlässlichste Zeichen für einen zu langen Stichpunkt.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Der Text in den Platzhaltern",
              "1.4",
              "Lies deine Stichpunkte selbst durch: keiner länger als zehn Wörter, keiner mit Punkt am Ende.",
            );
          }

          const platzhalter = inhaltsplatzhalter(snapshot);
          if (platzhalter.length === 0) {
            return fail(
              "In keinem Inhaltsplatzhalter steht Text.",
              "Schreibe zuerst ein paar Stichpunkte in den Inhaltsbereich einer Folie.",
            );
          }

          const zuLang = platzhalter.filter((form) => longestParagraphWords(form) > 10);
          const saetze = platzhalter.filter((form) =>
            form.paragraphs.some(looksLikeSentence),
          );

          if (zuLang.length === 0 && saetze.length === 0) {
            return pass(
              "Alle Stichpunkte sind kurz und keiner ist ein ausformulierter Satz – so bleibt Aufmerksamkeit für den Vortrag.",
            );
          }

          const beispiel = zuLang[0] ?? saetze[0];
          const laengster = [...beispiel.paragraphs].sort(
            (a, b) => countWords(b) - countWords(a),
          )[0];

          return fromCriteria(
            [
              criterion(
                zuLang.length === 0
                  ? "Kein Stichpunkt ist länger als zehn Wörter"
                  : `Zu lang auf Folie ${[...new Set(zuLang.map((form) => form.slideIndex + 1))].join(", ")}`,
                zuLang.length === 0,
              ),
              criterion(
                saetze.length === 0
                  ? "Kein Stichpunkt ist ein ausformulierter Satz"
                  : `Ganze Sätze auf Folie ${[...new Set(saetze.map((form) => form.slideIndex + 1))].join(", ")}`,
                saetze.length === 0,
              ),
            ],
            {
              success: "Die Stichpunkte sind kurz genug.",
              failure: `Auf Folie ${beispiel.slideIndex + 1} steht zum Beispiel: „${laengster.slice(0, 70)}“ (${countWords(laengster)} Wörter).`,
              hint: "Der ausformulierte Satz gehört in die Sprechernotizen – auf die Folie kommt nur der Kern.",
            },
          );
        },
      },
    },
    {
      id: "wie-viel-pro-folie",
      title: "Wie viel auf eine Folie passt",
      explanation: [
        "Die bekannte 6×6-Regel – höchstens sechs Stichpunkte mit je sechs Wörtern – ist " +
          "als Zahl willkürlich, als Größenordnung aber brauchbar. Ihr eigentlicher Wert " +
          "liegt darin, dass sie eine Entscheidung erzwingt: Was in diese Grenzen nicht " +
          "passt, ist nicht eine Folie, sondern zwei.",
        "Genau das ist der richtige Umgang mit zu viel Inhalt. Nicht die Schrift " +
          "verkleinern, nicht den Platzhalter vergrößern, sondern die Folie teilen. Zwei " +
          "Folien mit je vier Stichpunkten sind besser als eine mit acht – sie kosten im " +
          "Vortrag keine Sekunde mehr, weil du ohnehin nacheinander darüber sprichst.",
        "Eine gute Selbstkontrolle ist die Foliensortierung. Aus zwei Metern Abstand " +
          "sollte auf jeder Miniatur noch erkennbar sein, dass da Stichpunkte stehen und " +
          "kein grauer Textblock. Wenn eine Folie aus der Entfernung wie ein Absatz " +
          "aussieht, ist sie einer.",
      ],
      keyPoints: [
        "6×6 ist keine Regel, sondern eine Größenordnung.",
        "Zu viel Inhalt heißt teilen, nicht verkleinern.",
        "Foliensortierung aus zwei Metern Abstand als Selbstkontrolle.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass in keinem Inhaltsplatzhalter mehr als sechs Stichpunkte " +
          "stehen – teile eine zu volle Folie lieber auf.",
        hint: "Strg + M legt die zweite Folie an, der Titel bekommt dann meist einen Zusatz wie „(2)“.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Der Text in den Platzhaltern",
              "1.4",
              "Zähle die Stichpunkte auf deinen Folien selbst nach – höchstens sechs pro Platzhalter.",
            );
          }

          const platzhalter = inhaltsplatzhalter(snapshot);
          if (platzhalter.length === 0) {
            return fail(
              "In keinem Inhaltsplatzhalter steht Text.",
              "Schreibe zuerst Stichpunkte in den Inhaltsbereich einer Folie.",
            );
          }

          const zuVoll = platzhalter.filter((form) => form.paragraphs.length > 6);
          if (zuVoll.length === 0) {
            const maximum = Math.max(...platzhalter.map((form) => form.paragraphs.length));
            return pass(
              `Keine Folie ist überladen – der vollste Platzhalter hat ${maximum} Stichpunkte.`,
            );
          }

          return fail(
            `Auf Folie ${zuVoll[0].slideIndex + 1} stehen ${zuVoll[0].paragraphs.length} Stichpunkte in einem Platzhalter.` +
              (zuVoll.length > 1 ? ` Insgesamt betrifft das ${zuVoll.length} Platzhalter.` : ""),
            "Teile die Folie: Die Hälfte der Stichpunkte auf eine neue Folie mit passendem Titel. Die Schrift zu verkleinern verschiebt das Problem nur.",
          );
        },
      },
    },
  ],
};
